import { i18n } from '@/i18n'

/**
 * Факсимиле из ЛК (SSO user): должность + ФИО, опционально скан между ними.
 *
 * @typedef {{ dataUrl: string, w: number, h: number }} SignImage
 * @typedef {{ width: number, offsetX: number, offsetY: number }} SignImageLayout
 * @typedef {{
 *   jobTitle: string,
 *   signView: string,
 *   signImage: SignImage | null,
 *   signImageLayout: SignImageLayout,
 * }} FacsimilePayload
 */

export const DEFAULT_SIGN_IMAGE_WIDTH = 140
export const MIN_SIGN_IMAGE_WIDTH = 48
export const MAX_SIGN_IMAGE_WIDTH = 420
/** Лимит data URL (~220 KB) — JSONB на SSO. */
export const MAX_SIGN_IMAGE_DATA_URL_CHARS = 300_000

/**
 * Отступ рамки факсимиле от края страницы (как PAGE_EDGE_MARGIN в signatureModal).
 * Единый лимит ширины для ЛК, модалок подписания и PNG→PDF.
 */
export const FACSIMILE_PAGE_EDGE_MARGIN_PX = 10
/** Ширина A4 в CSS px при PDF.js scale=1 (1pt ≈ 1px). */
export const FACSIMILE_A4_WIDTH_PX = Math.round((210 / 25.4) * 72) // 595
/** Макс. ширина рамки: A4 − 2×отступ − 5px. */
export const FACSIMILE_FRAME_MAX_WIDTH_PX =
  FACSIMILE_A4_WIDTH_PX - FACSIMILE_PAGE_EDGE_MARGIN_PX * 2 - 5 // 570

/**
 * @returns {SignImageLayout}
 */
export function defaultSignImageLayout() {
  return { width: DEFAULT_SIGN_IMAGE_WIDTH, offsetX: 0, offsetY: 0 }
}

/**
 * @returns {FacsimilePayload}
 */
export function emptyFacsimile() {
  return {
    jobTitle: '',
    signView: '',
    signImage: null,
    signImageLayout: defaultSignImageLayout(),
  }
}

/**
 * @param {unknown} layout
 * @returns {SignImageLayout}
 */
export function parseSignImageLayout(layout) {
  const d = defaultSignImageLayout()
  if (!layout || typeof layout !== 'object') return d
  const width = Number(layout.width)
  const offsetX = Number(layout.offsetX)
  const offsetY = Number(layout.offsetY)
  return {
    width: Number.isFinite(width)
      ? Math.min(MAX_SIGN_IMAGE_WIDTH, Math.max(MIN_SIGN_IMAGE_WIDTH, width))
      : d.width,
    offsetX: Number.isFinite(offsetX) ? offsetX : 0,
    offsetY: Number.isFinite(offsetY) ? offsetY : 0,
  }
}

/**
 * @param {unknown} img
 * @returns {SignImage | null}
 */
export function parseSignImage(img) {
  if (!img || typeof img !== 'object') return null
  const dataUrl = String(img.dataUrl || '').trim()
  if (!dataUrl.startsWith('data:image/')) return null
  if (dataUrl.length > MAX_SIGN_IMAGE_DATA_URL_CHARS) return null
  const w = Number(img.w)
  const h = Number(img.h)
  return {
    dataUrl,
    w: Number.isFinite(w) && w > 0 ? Math.round(w) : 0,
    h: Number.isFinite(h) && h > 0 ? Math.round(h) : 0,
  }
}

/**
 * @param {unknown} v
 * @returns {FacsimilePayload | null}
 */
export function parseFacsimile(v) {
  if (v == null || typeof v !== 'object') return null
  const jobTitle = String(v.jobTitle ?? '').trim()
  const signView = String(v.signView ?? '').trim()
  const signImage = parseSignImage(v.signImage)
  // В ЛК LTMS остаются только скан; должность/ФИО необязательны, если есть изображение.
  if (!signImage && (!jobTitle || !signView)) return null
  const signImageLayout = parseSignImageLayout(v.signImageLayout)
  return {
    jobTitle,
    signView,
    signImage,
    signImageLayout: signImage ? signImageLayout : defaultSignImageLayout(),
  }
}

/**
 * Реактивный черновик ЛК → payload для SSO (или null если неполно).
 * @param {Partial<FacsimilePayload> | Record<string, unknown>} draft
 * @returns {FacsimilePayload | null}
 */
export function serializeFacsimile(draft) {
  return parseFacsimile(draft)
}

/**
 * Заполнить reactive-объект из сохранённого факсимиле.
 * @param {Record<string, unknown>} target
 * @param {unknown} source
 */
export function assignFacsimileDraft(target, source) {
  const p = parseFacsimile(source)
  const empty = emptyFacsimile()
  target.jobTitle = p?.jobTitle ?? ''
  target.signView = p?.signView ?? ''
  target.signImage = p?.signImage ? { ...p.signImage } : null
  target.signImageLayout = { ...(p?.signImageLayout ?? empty.signImageLayout) }
}

/**
 * Сжать скан подписи до PNG data URL с ограничением размера.
 * Только PNG (JPEG даёт серый/белый непрозрачный квадрат).
 * @param {File|Blob} file
 * @param {{ maxSide?: number }} [opts]
 * @returns {Promise<SignImage>}
 */
export async function compressSignatureImage(file, opts = {}) {
  const maxSideStart = opts.maxSide ?? 1200
  if (!file || !(file instanceof Blob)) {
    throw new Error(i18n.global.t('facsimile.errors.selectImage'))
  }
  if (!String(file.type || '').startsWith('image/')) {
    throw new Error(i18n.global.t('facsimile.errors.unsupportedImage'))
  }

  const bitmap = await createImageBitmap(file)
  try {
    const srcW = bitmap.width
    const srcH = bitmap.height
    if (!srcW || !srcH) throw new Error(i18n.global.t('facsimile.errors.readImage'))

    let maxSide = maxSideStart
    let dataUrl = ''
    let w = srcW
    let h = srcH

    while (maxSide >= 240) {
      const scale = Math.min(1, maxSide / Math.max(srcW, srcH))
      w = Math.max(1, Math.round(srcW * scale))
      h = Math.max(1, Math.round(srcH * scale))

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) throw new Error(i18n.global.t('facsimile.errors.canvasUnavailable'))
      ctx.clearRect(0, 0, w, h)
      ctx.drawImage(bitmap, 0, 0, w, h)
      makeLightPixelsTransparent(ctx, w, h)

      dataUrl = canvas.toDataURL('image/png')
      if (dataUrl.length <= MAX_SIGN_IMAGE_DATA_URL_CHARS) {
        return { dataUrl, w, h }
      }
      maxSide = Math.floor(maxSide * 0.72)
    }
    throw new Error(i18n.global.t('facsimile.errors.imageTooLarge'))
  } finally {
    bitmap.close?.()
  }
}

/**
 * Близкие к белому/светло-серому пиксели → alpha=0 (рамка/фон скана).
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w
 * @param {number} h
 */
export function makeLightPixelsTransparent(ctx, w, h) {
  try {
    const imageData = ctx.getImageData(0, 0, w, h)
    const d = imageData.data
    for (let i = 0; i < d.length; i += 4) {
      const r = d[i]
      const g = d[i + 1]
      const b = d[i + 2]
      const a = d[i + 3]
      if (a === 0) continue
      const maxc = Math.max(r, g, b)
      const minc = Math.min(r, g, b)
      if (minc >= 235 || (minc >= 210 && maxc - minc <= 12)) {
        d[i + 3] = 0
      }
    }
    ctx.putImageData(imageData, 0, 0)
  } catch {
    // cross-origin / security — оставляем как есть
  }
}

/**
 * Высота отображения скана по сохранённой ширине и aspect ratio.
 * @param {SignImage | null | undefined} img
 * @param {number} width
 */
export function signImageDisplayHeight(img, width) {
  const w = Number(width) || DEFAULT_SIGN_IMAGE_WIDTH
  const iw = Number(img?.w) || 0
  const ih = Number(img?.h) || 0
  if (iw > 0 && ih > 0) return Math.max(24, Math.round((w * ih) / iw))
  return Math.round(w * 0.45)
}

/** Дата в подписи: «15.05.2026 г.» */
export function formatSignatureDateLabel(d = new Date()) {
  const s = String(d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })).trim()
  if (/г\.?\s*$/i.test(s)) return s
  return `${s} г.`
}

/**
 * Вертикальные «вылеты» штампа относительно рамки (overflow_* в reply_doc_finalize).
 * @param {number} frameH
 * @param {number} drawH высота PNG на странице
 */
export function stampVerticalOverflowPx(frameH, drawH) {
  const fh = Math.max(0, Number(frameH) || 0)
  const dh = Math.max(0, Number(drawH) || 0)
  if (fh <= 0 || dh <= 0) return { top: 0, bottom: 0 }
  const imgTop = (fh - dh) / 2
  return {
    top: Math.max(0, -imgTop),
    bottom: Math.max(0, imgTop + dh - fh),
  }
}

/**
 * Оценка высоты PNG при экспорте, если рамка превью уже известна.
 * @param {number} frameH
 * @param {{signImage?: {dataUrl?: string}, signImageLayout?: object}|null|undefined} formDraft
 * @param {number} [scanSlotWidth]
 */
export function estimateStampDrawHeightPx(frameH, formDraft, scanSlotWidth) {
  const fh = Math.max(0, Number(frameH) || 0)
  if (fh <= 0) return 0
  const scan = formDraft?.signImage
  if (scan?.dataUrl) {
    const lay = parseSignImageLayout(formDraft?.signImageLayout)
    const slotW = Number(scanSlotWidth) || lay.width
    const scanH = signImageDisplayHeight(scan, slotW)
    return Math.max(fh, scanH + 48)
  }
  return Math.max(fh, Math.ceil(fh * 1.12))
}

/**
 * Проверка, что штамп целиком помещается на странице (hostSize при scale=1).
 * @param {object} opts
 * @returns {{fits: boolean, reason?: string}}
 */
export function validateStampPlacementOnPage(opts) {
  const {
    position,
    framePx = {},
    image = null,
    hostSize = {},
    pageEdgeMarginPx = FACSIMILE_PAGE_EDGE_MARGIN_PX,
  } = opts || {}

  const hostW = Number(hostSize.w) || 0
  const hostH = Number(hostSize.h) || 0
  if (hostW <= 0 || hostH <= 0) {
    return { fits: false, reason: 'no_host' }
  }

  const px = Number(position?.x)
  const py = Number(position?.y)
  if (!Number.isFinite(px) || !Number.isFinite(py)) {
    return { fits: false, reason: 'no_position' }
  }

  let fw = Number(framePx.w) || 0
  let fh = Number(framePx.h) || 0
  const iw = Number(image?.w) || 0
  const ih = Number(image?.h) || 0
  if (iw > 0 && (fw <= 0 || fw < iw)) {
    fw = Math.min(iw, FACSIMILE_FRAME_MAX_WIDTH_PX)
  }
  if (Number(image?.frameH) > 0) {
    fh = Math.max(fh, Number(image.frameH))
  }
  if (ih > 0 && fh <= 0) fh = ih
  if (fw <= 0) fw = Math.min(FACSIMILE_FRAME_MAX_WIDTH_PX, 360)
  if (fh <= 0) fh = 72

  const drawH = ih > 0 ? ih : fh
  const { top: overflowTop, bottom: overflowBottom } = stampVerticalOverflowPx(fh, drawH)

  const minX = pageEdgeMarginPx
  const maxX = hostW - fw - pageEdgeMarginPx
  const effectiveTop = py - overflowTop
  const effectiveBottom = py + fh + overflowBottom
  const eps = 1.5

  if (px < minX - eps || px > maxX + eps) {
    return { fits: false, reason: 'horizontal' }
  }
  if (effectiveTop < -eps) {
    return { fits: false, reason: 'above_page' }
  }
  if (effectiveBottom > hostH + eps) {
    return { fits: false, reason: 'below_page' }
  }
  return { fits: true }
}

/** Ключи ролей в родительном падеже для подписи «Факсимиле …». */
export const FACSIMILE_ROLE_GENITIVE = {
  facsimile_commerce: 'facsimile.roles.commerce',
  facsimile_coordinator: 'facsimile.roles.coordinator',
  facsimile_regspec: 'facsimile.roles.regspec',
  facsimile_reglead: 'facsimile.roles.reglead',
}

/** Заголовок блока в ЛК / карточке пользователя. */
export function facsimileBlockTitle(fieldKey) {
  const role = FACSIMILE_ROLE_GENITIVE[fieldKey]
  return role ? `${i18n.global.t('facsimile.title')} ${i18n.global.t(role)}` : i18n.global.t('facsimile.title')
}

/** Корневая подпись поля в истории (User.facsimile_*). */
export function facsimileHistoryRootLabel(fieldKey) {
  return facsimileBlockTitle(fieldKey)
}
